import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { loginUser } from '../../../api/apiServices';
import { useAuth } from '../../../context/useAuth';

export default function LoginPage() {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
  });

  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await loginUser(formData.email, formData.password);

      if (!res?.token) {
        setError(res?.message || 'Invalid email or password.');
        return;
      }

      // Login to context
      login(res.token, res.user);

      // Role-based navigation: Admin, SuperAdmin, and Instructor go straight to their dashboard
      const userRole = (res.user?.role || '').toLowerCase();

      if (userRole === 'admin' || userRole === 'administrator' || userRole === 'superadmin' || userRole === 'instructor') {
        navigate('/admin', { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    } catch (err) {
      console.error('Login error:', err);

      const errorMsg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        'Invalid credentials. Please check your email and password.';

      setError(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Sidebar */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-slate-950">
        <img
          src="https://readdy.ai/api/search-image?query=student%20studying%20online%20learning%20laptop%20modern%20bright%20room%20books%20warm%20lighting%20cozy%20productive%20atmosphere&width=800&height=900&seq=login1&orientation=portrait"
          alt="Student learning"
          className="w-full h-full object-cover object-top opacity-35 filter brightness-75"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-teal-950/85 to-slate-950/70 flex flex-col justify-end p-12">
          <Link to="/" className="inline-flex items-center gap-2 mb-8">
            <div className="w-10 h-10 rounded-xl bg-teal-500 flex items-center justify-center text-white text-xl font-bold shadow-md">
              <i className="ri-graduation-cap-fill" />
            </div>
            <span className="text-2xl font-bold text-white tracking-wide">LearnHub</span>
          </Link>
          <h2 className="text-3xl font-bold text-white mb-3 drop-shadow-md">Welcome back!</h2>
          <p className="text-slate-200 text-base leading-relaxed max-w-md drop-shadow-sm">
            Continue your learning journey and track your progress across all your courses.
          </p>

          <div className="flex gap-10 mt-10">
            {[
              { number: '50K+', label: 'Students' },
              { number: '4.9★', label: 'Rating' },
              { number: '200+', label: 'Courses' },
            ].map((item) => (
              <div key={item.label} className="text-center">
                <p className="text-3xl font-bold text-white tracking-tight">{item.number}</p>
                <p className="text-teal-300 text-sm font-medium mt-1">{item.label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 md:p-8">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden mb-8">
            <Link to="/" className="inline-flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-teal-600 flex items-center justify-center text-white text-xl font-bold">
                <i className="ri-graduation-cap-fill" />
              </div>
              <span className="text-xl font-bold text-slate-900">LearnHub</span>
            </Link>
          </div>

          <h1 className="text-2xl font-bold text-slate-900 mb-1">Sign in to LearnHub</h1>
          <p className="text-slate-500 mb-6">
            Don&apos;t have an account?{' '}
            <Link to="/signup" className="text-teal-600 font-semibold hover:text-teal-700">
              Sign up free
            </Link>
          </p>

          {/* Quick Demo Access */}
          <div className="mb-6 p-4 bg-slate-100/80 rounded-2xl border border-slate-200">
            <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2.5">
              🚀 Quick Demo Access
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => {
                  setFormData({ email: 'student@learnhub.com', password: 'password123' });
                  login('demo-student-token', {
                    _id: 'demo-student-1',
                    id: 'demo-student-1',
                    name: 'Alex Johnson',
                    userName: 'Alex Johnson',
                    email: 'student@learnhub.com',
                    role: 'student',
                    avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150&auto=format&fit=crop&q=80'
                  });
                  navigate('/dashboard');
                }}
                className="py-2 px-3 bg-white hover:bg-teal-50 border border-slate-200 hover:border-teal-300 rounded-xl text-xs font-medium text-slate-700 hover:text-teal-700 transition-colors flex items-center justify-center gap-1.5 shadow-sm"
              >
                <i className="ri-user-line text-teal-600" /> Demo Student
              </button>
              <button
                type="button"
                onClick={() => {
                  setFormData({ email: 'admin@learnhub.com', password: 'adminpassword' });
                  login('demo-admin-token', {
                    _id: 'demo-admin-1',
                    id: 'demo-admin-1',
                    name: 'Dr. Sarah Connor',
                    userName: 'Dr. Sarah Connor',
                    email: 'admin@learnhub.com',
                    role: 'admin',
                    avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=150&auto=format&fit=crop&q=80'
                  });
                  navigate('/admin');
                }}
                className="py-2 px-3 bg-white hover:bg-indigo-50 border border-slate-200 hover:border-indigo-300 rounded-xl text-xs font-medium text-slate-700 hover:text-indigo-700 transition-colors flex items-center justify-center gap-1.5 shadow-sm"
              >
                <i className="ri-shield-star-line text-indigo-600" /> Demo Instructor/Admin
              </button>
            </div>
          </div>

          {error && (
            <div className="bg-rose-50 border border-rose-200 text-rose-700 px-4 py-3 rounded-xl mb-6 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Email address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                placeholder="you@example.com"
                className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500 bg-white"
              />
            </div>

            {/* Password Field with Toggle */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  required
                  placeholder="Enter your password"
                  className="w-full px-4 py-3 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500 bg-white pr-12"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? 'Hide' : 'Show'}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white font-semibold rounded-xl transition-colors disabled:cursor-not-allowed"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-8 text-center">
            <Link to="/" className="text-sm text-slate-400 hover:text-slate-600">
              ← Back to home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}